'use client';

import { ReactNode } from 'react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { motion, AnimatePresence } from 'motion/react';

import Portal from 'app/components/common/Portal';
import IconCloseModal from 'app/components/sprites/IconCloseModal';
import LockBodyScroll from 'app/components/common/LockBodyScroll';

import 'overlayscrollbars/overlayscrollbars.css';
import classNames from 'classnames';

interface IModalProps {
  children: ReactNode;
  isOpen?: boolean;
  onClose: () => void;
}

export default function Modal({
  children,
  isOpen = false,
  onClose
}: IModalProps) {
  return (
    <Portal>
      <div
        className={classNames(
          'fixed z-100 top-0 bottom-0 w-full h-full overflow-hidden',
          {
            'translate-y-full opacity-0 delay-300': !isOpen,
            'translate-y-0 opacity-100 delay-0': isOpen
          }
        )}
      >
        {isOpen && <LockBodyScroll />}
        <motion.div
          initial="closed"
          animate={isOpen ? 'open' : 'closed'}
          exit="closed"
          variants={{
            open: { opacity: 1, transition: { duration: 0.3 } },
            closed: {
              opacity: 0,
              transition: { duration: 0.3 }
            }
          }}
          className="absolute top-0 left-0 w-full h-full bg-black-30"
          onClick={onClose}
        />
        <motion.div
          className="absolute z-10 top-20 bottom-0 left-0 right-0 bg-white rounded-xl"
          initial="closed"
          animate={isOpen ? 'open' : 'closed'}
          exit="closed"
          variants={{
            open: { y: 0, transition: { duration: 0.3 } },
            closed: { y: '100%', transition: { duration: 0.3 } }
          }}
        >
          <OverlayScrollbarsComponent
            defer
            className="absolute top-0 left-0 w-full h-full pb-3 [&_.os-scrollbar_.os-scrollbar-track]:bg-brown-20 [&_.os-scrollbar_.os-scrollbar-track]:w-[3px] [&_.os-scrollbar-handle]:bg-brown [&_.os-scrollbar-handle]:w-[3px]"
          >
            {children}
            <button
              type="button"
              className="absolute z-10 top-3 right-4 p-3"
              onClick={onClose}
            >
              <IconCloseModal />
            </button>
          </OverlayScrollbarsComponent>
        </motion.div>
      </div>
    </Portal>
  );
}
